import { Payment, StripeCard } from "@/models/Payment";

/**
 * Merges the tenders that paid for an order into the rows printed on a shipping receipt.
 *
 * The tenders arrive from two places and neither one is complete on its own. Card brand and last
 * four are not stored locally at all — they come back live from Stripe — while cash and check are
 * only ever sentinel rows in the Payments table that the Stripe lookup deliberately skips. On
 * Account (AR) is a third amount that lives in the ledger. Merging them here keeps the component
 * free of the reconciliation rules and makes them testable.
 */

const CASH_PREFIX = "cash";
const CHECK_PREFIX = "check";

/**
 * Stripe reports the brand differently depending on which API returned the card: the modern
 * PaymentMethods API yields lowercase ("visa", "american_express") while the legacy Card API yields
 * capitalized and spaced values ("American Express"). Normalize both to a single printable word.
 */
const BRAND_LABELS: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    americanexpress: "American Express",
    discover: "Discover",
    diners: "Diners Club",
    dinersclub: "Diners Club",
    jcb: "JCB",
    unionpay: "UnionPay",
    // Card-present charges with no readable brand fall back to "Reader" on the server.
    reader: "Card Reader",
};

export type ReceiptTenderKind = "card" | "cash" | "check" | "account" | "refund" | "authorized";

export interface ReceiptPaymentRow {
    /** Stable row identity for the table; never displayed. */
    key: string;
    kind: ReceiptTenderKind;
    /** The tender as printed: "Visa", "Cash", "Check", "On Account (AR)", "Refund". */
    method: string;
    /** "•••• 4242" for cards, "#1001" for checks, empty otherwise. */
    detail: string;
    /** "09/2027" for cards, empty otherwise. */
    expiration: string;
    /** Signed dollars. Refunds are negative so the printed total nets out. */
    amount: number;
}

/**
 * The brand as a printable word. The print window loads only Bootstrap, so a Font Awesome brand
 * icon would come out blank on paper — the receipt has to carry the brand as text.
 */
export function receiptBrandLabel(brand?: string | null): string {
    const raw = (brand ?? "").trim();
    if (!raw) return "Card";
    return BRAND_LABELS[raw.toLowerCase().replace(/[\s_-]/g, "")] ?? raw;
}

function toAmount(value: unknown): number {
    return typeof value === "number" && isFinite(value) ? value : 0;
}

function pad2(value: number): string {
    return String(value).padStart(2, "0");
}

/** "check_1001" reads as "#1001"; the raw id keeps its casing so alphanumeric check numbers survive. */
function checkReference(chargeId: string): string {
    const reference = chargeId.slice(CHECK_PREFIX.length).replace(/^[_\-\s]+/, "");
    return reference ? `#${reference}` : "";
}

function isCashOrCheck(chargeId: string): boolean {
    const lower = chargeId.toLowerCase();
    return lower.startsWith(CASH_PREFIX) || lower.startsWith(CHECK_PREFIX);
}

/**
 * "Reader" is a server-side sentinel, not a last four: Stripe.GetCharges substitutes it when a
 * card-present charge exposes no readable digits. Printing "•••• Reader" would read as a card number.
 * The brand column already says "Card Reader" via BRAND_LABELS, so the detail is simply left empty.
 */
const READER_SENTINEL = "reader";

/**
 * A payment row that represents a card, whether or not Stripe returned anything for it.
 *
 * A card tender is normally identifiable by its charge id. It can also exist before that id is
 * written back — see OrderDetailsPage.recalculateSelectedPayments — in which case the Stripe card it
 * used is the only evidence it is a card at all. Both shapes have to count, or the tender is
 * classified as nothing and vanishes from the receipt entirely.
 */
function isCardTender(payment: Partial<Payment>): boolean {
    const chargeId = (payment.chargeId ?? "").trim();
    if (chargeId) return !isCashOrCheck(chargeId);
    return !!(payment.StripeCardId ?? "").trim();
}

function cardRows(cards: ReadonlyArray<Partial<StripeCard>>): ReceiptPaymentRow[] {
    const rows: ReceiptPaymentRow[] = [];
    const seen = new Set<string>();

    cards.forEach((card, index) => {
        const transactionId = (card.transactionId ?? "").trim();
        if (transactionId) {
            if (seen.has(transactionId)) return;
            seen.add(transactionId);
        }

        const last4 = (card.cardNumber ?? "").trim();
        const detail = last4 && last4.toLowerCase() !== READER_SENTINEL ? `•••• ${last4}` : "";
        const captured = toAmount(card.amountCaptured);
        const authorized = toAmount(card.amount);
        // An authorization is not money collected. CPHub authorizes then captures separately, and
        // StripeAuthorizationRelease/ReleasePaymentAsync can release an auth that was never captured --
        // so printing one as paid tells the patient they were charged for something never collected.
        // It still belongs on the receipt, but labelled, and excluded from the paid total below.
        const authorizedOnly = captured <= 0 && authorized > 0;
        const charged = captured > 0 ? captured : authorized;
        const refunded = toAmount(card.amountRefunded);
        const id = transactionId || last4 || String(index);
        const brand = receiptBrandLabel(card.brand);

        if (charged !== 0) {
            rows.push({
                key: `card:${id}`,
                kind: authorizedOnly ? "authorized" : "card",
                method: authorizedOnly ? `${brand} (authorized, not captured)` : brand,
                detail,
                expiration: card.expMonth && card.expYear ? `${pad2(card.expMonth)}/${card.expYear}` : "",
                amount: charged,
            });
        }

        // A refund stays on the receipt as its own negative line so the printed total matches what
        // the patient was actually out of pocket.
        if (refunded > 0) {
            rows.push({
                key: `refund:${id}`,
                kind: "refund",
                method: "Refund",
                detail,
                expiration: "",
                amount: -refunded,
            });
        }
    });

    return rows;
}

/**
 * Card rows recovered from the order's own payment records when Stripe returned nothing for them.
 *
 * Cards are normally printed from the Stripe lookup, because brand and last four exist nowhere else.
 * But three ordinary situations leave a real card tender with no Stripe card to print:
 *
 *  1. the Stripe call failed;
 *  2. it succeeded and returned an empty list -- Stripe.GetCharges skips a `pi_` payment whose
 *     LatestChargeId is empty, so a card can be missing from a 200 response with no error at all;
 *  3. the tender has no charge id yet, so no Stripe call was attempted for it.
 *
 * In all three the amount is still sitting on the local payment record. Printing that with the brand
 * and last four blank understates the *detail* on a receipt; printing nothing understates the *money*,
 * and a receipt confidently claiming $0.00 for an order the patient paid by card is the worse failure.
 */
function unmatchedCardRows(cards: ReadonlyArray<Partial<StripeCard>>,
                           payments: ReadonlyArray<Partial<Payment>>): ReceiptPaymentRow[] {
    const returned = new Set(
        cards.map(card => (card.transactionId ?? "").trim().toLowerCase()).filter(Boolean));

    const rows: ReceiptPaymentRow[] = [];
    const seen = new Set<string>();

    payments.forEach((payment, index) => {
        if (!isCardTender(payment)) return;

        const chargeId = (payment.chargeId ?? "").trim();
        if (chargeId && returned.has(chargeId.toLowerCase())) return;

        // Keyed on the Payments row id, so two distinct card tenders are never collapsed.
        const id = payment.id != null ? `id:${payment.id}` : `charge:${chargeId.toLowerCase() || index}`;
        if (seen.has(id)) return;
        seen.add(id);

        const amount = toAmount(payment.authorizedAmount);
        if (amount === 0) return;

        rows.push({
            key: `card-local:${id}`,
            kind: "card",
            method: "Card",
            detail: "",
            expiration: "",
            amount,
        });
    });

    return rows;
}

function cashAndCheckRows(payments: ReadonlyArray<Partial<Payment>>): ReceiptPaymentRow[] {
    const rows: ReceiptPaymentRow[] = [];
    const seen = new Set<string>();

    payments.forEach((payment, index) => {
        const chargeId = (payment.chargeId ?? "").trim();
        // Card tenders come from Stripe, not from here — reading them off the Payments table too
        // would print every card twice.
        if (!chargeId || !isCashOrCheck(chargeId)) return;

        const lower = chargeId.toLowerCase();
        // Dedupe on the Payments row id where there is one, not the charge id. Cash is written with the
        // literal charge id "cash" every time, and usp_Payment_Save only de-dupes ids matching
        // pi_%/ch_% -- so two genuine cash payments on one order are two rows both keyed "cash".
        // Keying on the charge id collapsed them and printed only the first amount, understating the
        // total by the second. Checks were unaffected because check_1001/check_1002 disambiguate.
        // usp_Payment_GetByOrderId selects p.*, so real rows always carry an id; the charge-id
        // fallback only covers a row that reached here without one, where it is the best key available.
        const id = payment.id != null ? `id:${payment.id}` : `charge:${lower}`;
        if (seen.has(id)) return;
        seen.add(id);

        const amount = toAmount(payment.authorizedAmount);
        if (amount === 0) return;

        const cash = lower.startsWith(CASH_PREFIX);
        rows.push({
            key: `${cash ? "cash" : "check"}:${id}`,
            kind: cash ? "cash" : "check",
            method: cash ? "Cash" : "Check",
            detail: cash ? "" : checkReference(chargeId),
            expiration: "",
            amount,
        });
    });

    return rows;
}

export function buildReceiptPaymentRows(cards: ReadonlyArray<Partial<StripeCard>> | null | undefined,
                                        payments: ReadonlyArray<Partial<Payment>> | null | undefined,
                                        arPosted: number | null | undefined): ReceiptPaymentRow[] {
    const rows = [...cardRows(cards ?? []),
                  ...unmatchedCardRows(cards ?? [], payments ?? []),
                  ...cashAndCheckRows(payments ?? [])];

    const onAccount = toAmount(arPosted);
    if (onAccount > 0) {
        rows.push({
            key: "ar",
            kind: "account",
            method: "On Account (AR)",
            detail: "",
            expiration: "",
            amount: onAccount,
        });
    }

    return rows;
}

/**
 * True when a card tender had to be printed from the local payment record because Stripe returned no
 * card for it. The receipt shows the amount but not the brand or last four, and should say so.
 */
export function receiptHasCardDetailGap(rows: ReadonlyArray<ReceiptPaymentRow>): boolean {
    return rows.some(row => row.kind === "card" && row.method === "Card" && !row.detail);
}

/** True when an authorization that was never captured is on the receipt. */
export function receiptHasUncapturedAuthorization(rows: ReadonlyArray<ReceiptPaymentRow>): boolean {
    return rows.some(row => row.kind === "authorized");
}

/**
 * What the patient actually paid. Authorizations are excluded: an auth is a hold, it can be released
 * without ever being captured, and totalling it would tell the patient they paid money that was never
 * collected. Those rows still print, labelled, so the receipt is not silently missing a line.
 */
export function receiptTotalPaid(rows: ReadonlyArray<ReceiptPaymentRow>): number {
    return rows.reduce((total, row) => row.kind === "authorized" ? total : total + row.amount, 0);
}
